import React, { useEffect, useRef, useState } from 'react';
import { Modal, Row, Col, Card, Table, Button, Form, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SignatureCanvas from 'react-signature-canvas';
import Moment from 'moment';
import Swal from 'sweetalert2';
import { apiHandler } from '../../api/apihandler';
import { handleImageUpload } from '../../api/uploads';
import {
  TEACHERS_MEDICATION_API,
  TEACHERS_MEDICATION_LOG_API,
  TEACHERS_MEDICATION_LOG_STORE_API,
} from '../../api/constants';
import { formatDisplayDate, formatDisplayTime } from '../../utilities/displayFormat';
import MedicationRecordList, { bucketAndSortMedicationRecords } from './shared/MedicationRecordList';

const EMPTY_LOG_FORM = {
  medication_date: new Date().toISOString().split('T')[0],
  medication: '',
  is_dosage_given: 1,
  time_given: new Date().toTimeString().slice(0, 5),
  staff_signiture: '',
  comments: '',
};

// The real "Medication Overview" modal, shared between the Attendance page
// and notification.js (Medication Administered notification) so a teacher
// sees and manages the exact same thing from either entry point. Self-
// contained: fetches its own data from studentId and owns its own log
// add/edit/delete state, instead of relying on the Attendance page's other
// state the way the original inline version did.
const MedicationModal = ({ show, onHide, studentId, studentName }) => {
  const [records, setRecords] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showLogFormModal, setShowLogFormModal] = useState(false);
  const [isEditingLog, setIsEditingLog] = useState(false);
  const [logFormData, setLogFormData] = useState(EMPTY_LOG_FORM);
  const [isSavingLog, setIsSavingLog] = useState(false);
  const signaturePadRef = useRef(null);

  const [showLogViewModal, setShowLogViewModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  async function loadMedication() {
    if (!studentId) return;
    setLoading(true);
    try {
      const response = await apiHandler(TEACHERS_MEDICATION_API, 'POST', {
        student_id: studentId,
      });
      if (response.data?.response?.status === 200 && response.data.data) {
        const { student: studentData, medications, medication_logs } = response.data.data;
        const rawRecords = Array.isArray(medications) ? medications : [];
        const resolvedName = studentData?.name || studentName || '';
        const [firstName, ...restName] = resolvedName.split(' ');
        setRecords(bucketAndSortMedicationRecords(rawRecords).map((r) => ({
          ...r,
          _detail: {
            first_name: firstName || '',
            last_name: restName.join(' '),
            parent_name: r.parent_name || 'N/A',
            medication_name: r.medication_name || 'N/A',
            times_to_be_administered: r.times_to_be_administered
              ? (r.times_to_be_administered.includes(':') ? formatDisplayTime(r.times_to_be_administered) : r.times_to_be_administered)
              : 'N/A',
            date_start: r.date_start || null,
            date_end: r.date_end || null,
            expiry_date: r.expiry_date || null,
            parents_consent_and_instruction: r.parents_consent_and_instruction || 'N/A',
            additional_instructions: r.additional_instructions || 'N/A',
            reason_for_administering: r.reason_for_administering || 'N/A',
            doctor_note: r.doctor_note || null,
            signiture_of_parent: r.signiture_of_parent || null,
            signature_date: formatDisplayDate(r.signature_date),
            medication_id: r.id,
          },
        })));
        setLogs(Array.isArray(medication_logs) ? medication_logs : []);
      } else {
        setRecords([]);
        setLogs([]);
      }
    } catch (error) {
      console.error('Error loading medication:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!show || !studentId) {
      return;
    }
    setSelectedRecordId(null);
    loadMedication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, studentId]);

  const selectedRecord = records.find((r) => r.id === selectedRecordId) || null;
  const medicationData = selectedRecord?._detail || null;
  const recordLogs = selectedRecordId
    ? logs.filter((log) => !log.medication_id || log.medication_id === selectedRecordId)
    : logs;

  function openAddLogForm() {
    setIsEditingLog(false);
    setLogFormData({ ...EMPTY_LOG_FORM, medication: medicationData?.medication_name || '' });
    if (signaturePadRef.current) signaturePadRef.current.clear();
    setShowLogFormModal(true);
  }

  function openEditLogForm(log) {
    setLogFormData({
      id: log.id,
      medication_date: log.medication_date ? Moment(log.medication_date).format('YYYY-MM-DD') : '',
      medication: log.medication || '',
      is_dosage_given: log.is_dosage_given || 1,
      time_given: log.time_given || '',
      staff_signiture: log.staff_signiture || '',
      comments: log.comments || '',
    });
    setIsEditingLog(true);
    setShowLogFormModal(true);
  }

  async function saveLog() {
    if (!logFormData.medication_date || !logFormData.medication) {
      Swal.fire({ title: 'Validation Error', text: 'Please fill in medication date and medication name', icon: 'warning', confirmButtonText: 'OK' });
      return;
    }

    try {
      setIsSavingLog(true);

      let signatureUrl = logFormData.staff_signiture;
      const hasNewSignature = signaturePadRef.current && !signaturePadRef.current.isEmpty();
      if (hasNewSignature) {
        const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
        const signatureBlob = await fetch(signatureDataURL).then((res) => res.blob());
        const signatureFile = new File([signatureBlob], `staff_signature_${Date.now()}.png`, { type: 'image/png' });
        signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
      }

      const payload = {
        user_id: studentId,
        administration_medication_id: medicationData?.medication_id,
        medication_date: logFormData.medication_date,
        medication: logFormData.medication,
        is_dosage_given: logFormData.is_dosage_given,
      };
      if (logFormData.time_given) payload.time_given = logFormData.time_given;
      if (signatureUrl) payload.staff_signiture = signatureUrl;
      if (logFormData.comments) payload.comments = logFormData.comments;

      let response;
      if (isEditingLog && logFormData.id) {
        response = await apiHandler(`${TEACHERS_MEDICATION_LOG_API}${logFormData.id}`, 'POST', payload);
      } else {
        response = await apiHandler(TEACHERS_MEDICATION_LOG_STORE_API, 'POST', payload);
      }

      if (response.data?.response?.status === 200) {
        Swal.fire({ title: 'Success', text: isEditingLog ? 'Medication log updated successfully' : 'Medication log added successfully', icon: 'success', timer: 2000, showConfirmButton: false });
        setShowLogFormModal(false);
        setIsEditingLog(false);
        await loadMedication();
      } else {
        throw new Error(response.data?.response?.message || 'Failed to save medication log');
      }
    } catch (error) {
      console.error('Error saving medication log:', error);
      Swal.fire({ title: 'Error', text: error.message || 'Failed to save medication log', icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setIsSavingLog(false);
    }
  }

  async function deleteLog(logId) {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This medication log will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });
    if (!result.isConfirmed) return;

    try {
      const response = await apiHandler(`users/v1/attendance/medication/log/${logId}`, 'DELETE');
      if (response.data?.response?.status === 200) {
        Swal.fire({ title: 'Deleted!', text: 'Medication log has been deleted successfully.', icon: 'success', timer: 2000, showConfirmButton: false });
        await loadMedication();
      } else {
        Swal.fire({ title: 'Error', text: response.data?.response?.message || 'Failed to delete medication log', icon: 'error', confirmButtonText: 'OK' });
      }
    } catch (error) {
      console.error('Error deleting medication log:', error);
      Swal.fire({ title: 'Network Error', text: 'Unable to connect to the server. Please check your internet connection.', icon: 'error', confirmButtonText: 'OK' });
    }
  }

  return (
    <>
      <Modal show={show} onHide={() => { onHide(); setSelectedRecordId(null); }} size="xl" centered style={{ marginTop: '30px' }}>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '16px 20px', background: 'white' }}>
          <Modal.Title style={{ fontSize: '16px', fontWeight: '700', color: '#111827', lineHeight: '1.3', width: '100%', maxWidth: 'calc(100% - 40px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="ri-medicine-bottle-line" style={{ fontSize: '20px', color: '#6366f1' }}></i>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>Medication Overview</div>
                <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>
                  {medicationData ? `${medicationData.first_name} ${medicationData.last_name}` : (studentName || '')}
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px', background: '#f9fafb', maxHeight: '65vh', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading...
            </div>
          ) : selectedRecordId === null ? (
            <MedicationRecordList records={records} onSelect={(record) => setSelectedRecordId(record.id)} />
          ) : medicationData ? (
            <Row>
              <Col md={12}>
                {records.length > 1 && (
                  <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => setSelectedRecordId(null)}>
                    <i className="ri-arrow-left-line me-1"></i> Back to list
                  </Button>
                )}
                <Card style={{ borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '16px', background: 'white' }}>
                  <Card.Header style={{ background: 'white', borderBottom: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0', padding: '12px 16px', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                    <i className="ri-medicine-bottle-fill me-2" style={{ color: '#6366f1' }}></i>
                    Medication Information
                  </Card.Header>
                  <Card.Body style={{ padding: '16px' }}>
                    <Row>
                      <Col md={6}>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Student Name</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{medicationData.first_name} {medicationData.last_name}</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Parent Name</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{medicationData.parent_name || 'N/A'}</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Medication Name</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--bs-primary)' }}>{medicationData.medication_name || 'Not specified'}</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Times to be Administered</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{medicationData.times_to_be_administered || 'Not specified'}</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date Range</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{formatDisplayDate(medicationData.date_start)} to {formatDisplayDate(medicationData.date_end)}</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Expiry Date</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{formatDisplayDate(medicationData.expiry_date)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reason for Administering</div>
                          <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.5' }}>{medicationData.reason_for_administering || 'No reason specified'}</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Parent's Consent & Instructions</div>
                          <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.5' }}>{medicationData.parents_consent_and_instruction || 'No instructions provided'}</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Additional Instructions</div>
                          <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.5' }}>{medicationData.additional_instructions || 'No additional instructions'}</div>
                        </div>
                        {medicationData.signiture_of_parent && medicationData.signiture_of_parent !== 'null' && medicationData.signiture_of_parent !== '' && (
                          <div style={{ marginBottom: '15px' }}>
                            <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Parent Signature</div>
                            <img
                              src={medicationData.signiture_of_parent}
                              alt="Parent Signature"
                              onError={(e) => { e.target.style.display = 'none'; }}
                              style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px', background: 'white', display: 'block' }}
                            />
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>Signed on: {medicationData.signature_date}</div>
                          </div>
                        )}
                        {medicationData.doctor_note && (
                          <div style={{ marginBottom: '15px' }}>
                            <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Doctor's Note</div>
                            <a href={medicationData.doctor_note} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--bs-primary)' }}>
                              <i className="ri-file-text-line me-1"></i>
                              View Doctor's Note
                            </a>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12}>
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                  <Card.Header style={{
                    background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-primary) 100%)',
                    color: 'white',
                    borderRadius: '12px 12px 0 0',
                    padding: '16px 20px',
                    fontSize: '18px',
                    fontWeight: '600',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span>
                      <i className="ri-file-list-line me-2"></i>
                      Medication Log
                    </span>
                    <Button variant="light" size="sm" onClick={openAddLogForm} style={{ borderRadius: '8px', fontWeight: '600', padding: '6px 16px' }}>
                      <i className="ri-add-line me-1"></i>
                      Add Log Entry
                    </Button>
                  </Card.Header>
                  <Card.Body style={{ padding: '0' }}>
                    {recordLogs && recordLogs.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <Table hover style={{ margin: 0 }}>
                          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                            <tr>
                              <th style={{ padding: '16px', fontWeight: '600', color: '#333' }}>Date</th>
                              <th style={{ padding: '16px', fontWeight: '600', color: '#333' }}>Medication</th>
                              <th style={{ padding: '16px', fontWeight: '600', color: '#333' }}>Dosage Given</th>
                              <th style={{ padding: '16px', fontWeight: '600', color: '#333' }}>Time</th>
                              <th style={{ padding: '16px', fontWeight: '600', color: '#333' }}>Staff Signature</th>
                              <th style={{ padding: '16px', fontWeight: '600', color: '#333' }}>Comments</th>
                              <th style={{ padding: '16px', fontWeight: '600', color: '#333', textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recordLogs.map((log, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '16px', color: '#333' }}>{formatDisplayDate(log.medication_date)}</td>
                                <td style={{ padding: '16px', color: '#333', fontWeight: '600' }}>{log.medication || 'N/A'}</td>
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <span style={{
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    background: log.is_dosage_given === 1 || log.is_dosage_given === '1' ? '#d1fae5' : '#fee2e2',
                                    color: log.is_dosage_given === 1 || log.is_dosage_given === '1' ? '#065f46' : '#991b1b',
                                  }}>
                                    {log.is_dosage_given === 1 || log.is_dosage_given === '1' ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td style={{ padding: '16px', color: '#333' }}>
                                  {log.time_given ? Moment(log.time_given, 'HH:mm:ss').format('h:mm A') : 'N/A'}
                                </td>
                                <td style={{ padding: '16px', color: '#333' }}>
                                  {log.staff_signiture ? (
                                    <img src={log.staff_signiture} alt="Staff Signature" style={{ maxWidth: '80px', maxHeight: '40px' }} />
                                  ) : 'N/A'}
                                </td>
                                <td style={{ padding: '16px', color: '#666', maxWidth: '200px' }}>
                                  {log.comments && log.comments.length > 40 ? `${log.comments.substring(0, 40)}...` : log.comments || '-'}
                                </td>
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    <Button variant="outline-primary" size="sm" onClick={() => { setSelectedLog(log); setShowLogViewModal(true); }} style={{ borderRadius: '6px' }} title="View Log">
                                      <i className="ri-eye-line"></i>
                                    </Button>
                                    <Button variant="outline-success" size="sm" onClick={() => openEditLogForm(log)} style={{ borderRadius: '6px' }} title="Edit Log">
                                      <i className="ri-edit-line"></i>
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={() => deleteLog(log.id)} style={{ borderRadius: '6px' }} title="Delete Log">
                                      <i className="ri-delete-bin-line"></i>
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                        <i className="ri-medicine-bottle-line" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}></i>
                        <p style={{ fontSize: '16px', marginBottom: '8px' }}>No medication logs found</p>
                        <p style={{ fontSize: '14px', color: '#999' }}>Click "Add Log Entry" to record medication administration</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <i className="ri-medicine-bottle-line" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}></i>
              <p>No medication data available</p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showLogFormModal} onHide={() => {
        setShowLogFormModal(false);
        setIsEditingLog(false);
        if (signaturePadRef.current) signaturePadRef.current.clear();
      }} centered style={{ marginTop: '30px' }}>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '16px 20px', background: 'white' }}>
          <Modal.Title style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
            <i className={isEditingLog ? 'ri-edit-line me-2' : 'ri-add-circle-line me-2'} style={{ fontSize: '16px', color: '#6366f1' }}></i>
            {isEditingLog ? 'Edit Medication Log' : 'Add Medication Log'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px', background: 'white' }}>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                    <span style={{ color: '#dc3545' }}>* </span>Medication Date
                  </Form.Label>
                  <DatePicker
                    selected={logFormData.medication_date ? Moment(logFormData.medication_date).toDate() : null}
                    onChange={(date) => setLogFormData({ ...logFormData, medication_date: date ? Moment(date).format('YYYY-MM-DD') : '' })}
                    dateFormat="MMM d yyyy"
                    placeholderText="Dec 17 2025"
                    className="form-control"
                    required
                    style={{ borderRadius: '6px', border: '1px solid #e5e7eb', padding: '10px 12px', fontSize: '13px', width: '100%' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>Time Given</Form.Label>
                  <DatePicker
                    selected={logFormData.time_given ? Moment(logFormData.time_given, 'HH:mm').toDate() : null}
                    onChange={(time) => setLogFormData({ ...logFormData, time_given: time ? Moment(time).format('HH:mm') : '' })}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    placeholderText="2:30 PM"
                    className="form-control"
                    style={{ borderRadius: '6px', border: '1px solid #e5e7eb', padding: '10px 12px', fontSize: '13px', width: '100%' }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                <span style={{ color: '#dc3545' }}>* </span>Medication
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter medication name"
                value={logFormData.medication}
                onChange={(e) => setLogFormData({ ...logFormData, medication: e.target.value })}
                required
                style={{ borderRadius: '6px', border: '1px solid #e5e7eb', padding: '10px 12px', fontSize: '13px' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                <span style={{ color: '#dc3545' }}>* </span>Is Dosage Given?
              </Form.Label>
              <div style={{ display: 'flex', gap: '20px', padding: '12px 0' }}>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="is_dosage_given"
                  checked={logFormData.is_dosage_given === 1 || logFormData.is_dosage_given === '1'}
                  onChange={() => setLogFormData({ ...logFormData, is_dosage_given: 1 })}
                  style={{ fontSize: '14px', fontWeight: '500' }}
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="is_dosage_given"
                  checked={logFormData.is_dosage_given === 0 || logFormData.is_dosage_given === '0'}
                  onChange={() => setLogFormData({ ...logFormData, is_dosage_given: 0 })}
                  style={{ fontSize: '14px', fontWeight: '500' }}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#333', marginBottom: '8px' }}>Staff Signature (Optional)</Form.Label>
              {logFormData.staff_signiture && logFormData.staff_signiture !== 'null' && logFormData.staff_signiture !== '' ? (
                <div className="mb-2">
                  <img
                    src={logFormData.staff_signiture}
                    alt="Existing Signature"
                    style={{ maxWidth: '200px', maxHeight: '80px', border: '1px solid #e9ecef', borderRadius: '8px', padding: '8px', background: 'white' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="text-muted small mt-1">Existing signature (will be replaced if you draw a new one)</div>
                </div>
              ) : null}
              <div style={{ border: '2px solid #e9ecef', borderRadius: '8px', padding: '8px', background: 'white', marginBottom: '8px' }}>
                <SignatureCanvas
                  ref={signaturePadRef}
                  canvasProps={{ style: { width: '100%', height: '150px', border: '1px dashed #dee2e6', borderRadius: '4px' } }}
                />
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => { if (signaturePadRef.current) signaturePadRef.current.clear(); }}
                type="button"
                style={{ borderRadius: '6px' }}
              >
                <i className="ri-eraser-line me-1"></i>
                Clear Signature
              </Button>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#333', marginBottom: '8px' }}>Comments (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Any additional comments or observations"
                value={logFormData.comments}
                onChange={(e) => setLogFormData({ ...logFormData, comments: e.target.value })}
                style={{ borderRadius: '8px', border: '2px solid #e9ecef', padding: '12px 16px', fontSize: '14px' }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none', padding: '16px 24px', background: '#fafbfc' }}>
          <Button
            variant="secondary"
            onClick={() => {
              setShowLogFormModal(false);
              setIsEditingLog(false);
              if (signaturePadRef.current) signaturePadRef.current.clear();
            }}
            style={{ borderRadius: '8px', padding: '8px 20px', fontSize: '14px', fontWeight: '500' }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={saveLog} disabled={isSavingLog} style={{ borderRadius: '8px', padding: '8px 20px', fontSize: '14px', fontWeight: '600' }}>
            {isSavingLog ? (
              <>
                <i className="ri-loader-4-line ri-spin me-2"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line me-2"></i>
                Save Log Entry
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showLogViewModal} onHide={() => setShowLogViewModal(false)} centered style={{ marginTop: '50px' }}>
        <Modal.Header closeButton style={{ borderBottom: '3px solid var(--bs-primary)', padding: '16px 24px', background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-primary) 100%)' }}>
          <Modal.Title style={{ fontSize: '20px', fontWeight: '800', color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <i className="ri-file-text-line me-3"></i>
            Medication Log Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px', background: '#fafbfc' }}>
          {selectedLog ? (
            <div>
              <Row className="mb-3">
                <Col sm={4}><strong style={{ color: '#333', fontSize: '14px' }}>Medication Date:</strong></Col>
                <Col sm={8}><span style={{ color: '#666' }}>{formatDisplayDate(selectedLog.medication_date)}</span></Col>
              </Row>
              <Row className="mb-3">
                <Col sm={4}><strong style={{ color: '#333', fontSize: '14px' }}>Medication:</strong></Col>
                <Col sm={8}><span style={{ color: '#666', fontWeight: '600' }}>{selectedLog.medication || 'N/A'}</span></Col>
              </Row>
              <Row className="mb-3">
                <Col sm={4}><strong style={{ color: '#333', fontSize: '14px' }}>Dosage Given:</strong></Col>
                <Col sm={8}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: selectedLog.is_dosage_given === 1 || selectedLog.is_dosage_given === '1' ? '#d1fae5' : '#fee2e2',
                    color: selectedLog.is_dosage_given === 1 || selectedLog.is_dosage_given === '1' ? '#065f46' : '#991b1b',
                    display: 'inline-block',
                  }}>
                    {selectedLog.is_dosage_given === 1 || selectedLog.is_dosage_given === '1' ? 'Yes' : 'No'}
                  </span>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm={4}><strong style={{ color: '#333', fontSize: '14px' }}>Time Given:</strong></Col>
                <Col sm={8}><span style={{ color: '#666' }}>{selectedLog.time_given ? Moment(selectedLog.time_given, 'HH:mm:ss').format('h:mm A') : 'N/A'}</span></Col>
              </Row>
              {selectedLog.staff_signiture && selectedLog.staff_signiture !== 'null' && selectedLog.staff_signiture !== '' && (
                <Row className="mb-3">
                  <Col sm={4}><strong style={{ color: '#333', fontSize: '14px' }}>Staff Signature:</strong></Col>
                  <Col sm={8}>
                    <img
                      src={selectedLog.staff_signiture}
                      alt="Staff Signature"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      style={{ maxWidth: '150px', maxHeight: '75px', border: '1px solid #dee2e6', borderRadius: '4px', padding: '4px', background: 'white', display: 'block' }}
                    />
                  </Col>
                </Row>
              )}
              {selectedLog.comments && (
                <Row className="mb-3">
                  <Col sm={4}><strong style={{ color: '#333', fontSize: '14px' }}>Comments:</strong></Col>
                  <Col sm={8}><span style={{ color: '#666' }}>{selectedLog.comments}</span></Col>
                </Row>
              )}
              <Row className="mb-3">
                <Col sm={4}><strong style={{ color: '#333', fontSize: '14px' }}>Recorded On:</strong></Col>
                <Col sm={8}>
                  <span style={{ color: '#666' }}>
                    {selectedLog.created_at ? `${formatDisplayDate(selectedLog.created_at)} ${formatDisplayTime(selectedLog.created_at)}` : 'Not available'}
                  </span>
                </Col>
              </Row>
            </div>
          ) : (
            <p>No log details available</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MedicationModal;
